import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import styles from "./GooglePlacesInput.module.css"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { Dropdown, DropdownToggle } from "reactstrap"
import { DropdownItem, DropdownMenu } from "components/common/Dropdown"
import Input from "components/form/Input"
import usePredictions from "modules/google/hooks/usePredictions"
import DynamicTag from "components/DynamicTag"
import { useDispatch, useSelector } from "react-redux"
import { addRecentSearch, removeRecentSearch } from "store/location"
import { ClockCircleOutlined, CloseOutlined, EnvironmentOutlined, CompassOutlined } from "@ant-design/icons"

const GooglePlacesInput = (
  {
    tag,
    onInput,
    onChange,
    dropdownClassName,
    wrapperProps = null,
    dropDownMenuClassName,
    dropDownMenuProps = {},
    resetIfChanged = false,
    useDefaultValue = false,
    innerRef,
    name,
    ...rest
  },
  ref
) => {
  const inputRef = useRef(null)
  const { t } = useTranslation()
  const [predictions, place, getPredictions, selectPlace] = usePredictions()
  const dispatch = useDispatch()
  const recentSearches = useSelector((state) => state.location.recentSearches || [])
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const dropdownRef = useRef(null)

  const coordinates = useMemo(() => {
    if (!place) return
    const location = place?.geometry?.location
    if (!location) return
    const lat = location.lat()
    const lng = location.lng()
    return { lat, lng }
  }, [place])

  const handleKeyPress = (event) => event.key === "Enter" && event.preventDefault()
  const changeHandler = (event) => {
    if (event.persist) event.persist()
    setInputValue(event.target.value)
    getPredictions(event.target.value)
  }

  const handleClear = () => {
    setInputValue("")
    if (inputRef.current) inputRef.current.value = ""
    getPredictions("")
    if (onChange !== undefined && typeof onChange === "function") {
      onChange({ target: { name, value: "", place: null, coordinates: undefined } })
    }
  }

  const clickHandler = (prediction) => (event) => {
    if (event.persist) event.persist()
    selectPlace(prediction.place_id, (selectedPlace) => {
      if (useDefaultValue) return
      // If value is controlled, update via onChange only
      if (rest.value !== undefined && onChange !== undefined && typeof onChange === "function") {
        const coordinates = selectedPlace?.geometry?.location
        onChange({
          target: {
            name,
            value: selectedPlace.formatted_address,
            place: selectedPlace,
            coordinates: coordinates ? { lat: coordinates.lat(), lng: coordinates.lng() } : undefined
          }
        })
      } else {
        inputRef.current.value = resetIfChanged ? "" : selectedPlace.formatted_address
      }
      dispatch(addRecentSearch({ place_id: prediction.place_id, description: prediction.description }))
      setIsOpen(false)
    })
  }

  const recentClickHandler = (search) => (event) => {
    if (event.persist) event.persist()
    selectPlace(search.place_id, (selectedPlace) => {
      if (useDefaultValue) return
      inputRef.current.value = resetIfChanged ? "" : search.description
      setIsOpen(false)
    })
  }

  const removeRecentHandler = (place_id) => (event) => {
    event.stopPropagation()
    dispatch(removeRecentSearch(place_id))
  }

  useEffect(() => {
    if (useDefaultValue && inputRef.current) inputRef.current.value = rest.defaultValue
    setInputValue(rest.defaultValue || "")
  }, [rest.defaultValue]) //eslint-disable-line

  useEffect(() => {
    if (onInput !== undefined && typeof onInput === "function") onInput({ target: { name, value: inputRef.current.value, predictions } })
  }, [predictions, onInput]) //eslint-disable-line

  useEffect(() => {
    if (!coordinates) return
    if (onChange !== undefined && typeof onChange === "function")
      onChange({ target: { name, value: place.formatted_address, place, coordinates } })
  }, [coordinates?.lat, coordinates?.lng]) //eslint-disable-line

  useEffect(() => {
    if (ref) ref.current = inputRef.current
  }, [ref])

  useEffect(() => {
    if (!!predictions?.length || !!recentSearches.length) setIsOpen(true)
  }, [predictions, recentSearches])

  // Click-away handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <DynamicTag tag={wrapperProps ? "div" : React.Fragment} {...wrapperProps}>
      <div className={styles.inputWrapper}>
        <DynamicTag
          {...rest}
          tag={tag || Input}
          type="text"
          onKeyPress={handleKeyPress}
          onChange={changeHandler}
          ref={inputRef}
          onFocus={() => setIsOpen(true)}
          value={inputValue}
        />
        {inputValue && <CloseOutlined className={styles.clearIcon} onClick={handleClear} aria-label="Clear" />}
      </div>
      <Dropdown
        direction="down"
        isOpen={isOpen}
        toggle={() => setIsOpen((open) => !open)}
        innerRef={dropdownRef}
        ref={inputRef}
        onFocus={() => setIsOpen(true)}
      >
        <DropdownToggle className="position-absolute p-0 border-0 opacity-0 pointer-events-none" />
        <DropdownMenu {...dropDownMenuProps} container="body">
          <div className={`vstack ${styles.menuMinWidth}`}>
            {recentSearches?.length > 0 && (
              <div className="dropdown-header fw-bold px-3 pt-2 pb-1">{t("googlePlacesInput.recent_searches")}</div>
            )}
            {recentSearches?.map((search) => (
              <DropdownItem
                key={search.place_id}
                className="d-flex align-items-center justify-content-between"
                onClick={recentClickHandler(search)}
              >
                <span>
                  <ClockCircleOutlined className={styles.predictionIcon} />
                  {search.description}
                </span>
                <CloseOutlined className={styles.recentCloseIcon} onClick={removeRecentHandler(search.place_id)} />
              </DropdownItem>
            ))}
            <div className="dropdown-divider my-1" />
            <div className="dropdown-header fw-bold px-3 pt-2 pb-1">{t("googlePlacesInput.recommendations")}</div>
            <DropdownItem className="d-flex align-items-center text-muted" disabled>
              <CompassOutlined className={styles.predictionIcon} />
              {t("googlePlacesInput.near_you")} <span className="ms-2">{t("googlePlacesInput.search_locations_near_you")}</span>
            </DropdownItem>
            {predictions?.map((prediction) => (
              <DropdownItem className="d-flex align-items-center text-wrap" onClick={clickHandler(prediction)} key={prediction.place_id}>
                <EnvironmentOutlined className={styles.predictionIcon} />
                {prediction.description}
              </DropdownItem>
            ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </DynamicTag>
  )
}

GooglePlacesInput.propTypes = {
  tag: PropTypes.elementType,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  dropdownClassName: PropTypes.string,
  wrapperProps: PropTypes.object,
  dropDownMenuClassName: PropTypes.string,
  dropDownMenuProps: PropTypes.object,
  resetIfChanged: PropTypes.bool,
  useDefaultValue: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  name: PropTypes.string
}
export default forwardRef(GooglePlacesInput)
